import _ from "lodash/fp";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useResizeObserver from "@react-hook/resize-observer";
import { getSelectOptionsFromMetadata } from "./utils";

import { useSessionStorage } from "./useSessionStorage";

import { store as DataGridStore } from "../stores/datagrid";
import { store as ItemStore } from "../stores/item";
import { store as PassageStore } from "../stores/passage";
import { store as SearchStore } from "../stores/search";
import { store as WorkspaceLayoutStore } from "../stores/workspace";

const useDraggableInPortal = () => {
  const self = useRef({}).current;

  useEffect(() => {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.pointerEvents = "none";
    div.style.top = "0";
    div.style.width = "100%";
    div.style.height = "100%";
    self.elt = div;
    document.body.appendChild(div);
    return () => {
      document.body.removeChild(div);
    };
  }, [self]);

  return (render) =>
    (provided, ...args) => {
      const element = render(provided, ...args);
      if (provided.draggableProps.style.position === "fixed") {
        return createPortal(element, self.elt);
      }
      return element;
    };
};

const useGetMetadataConfigurationOptions = (
  metadataConfiguration,
  programs = [],
  type,
  path = [],
) => {
  const options = useMemo(() => {
    if (!metadataConfiguration || !programs) return [];
    return path.map((p) =>
      getSelectOptionsFromMetadata(metadataConfiguration)(programs)(type)(p),
    );
  }, [metadataConfiguration, path, programs, type]);
  return options;
};

const useFindPreviousValue = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const useGetProgramsByUser = (user, programs = []) => {
  return useMemo(
    () =>
      programs.filter(({ id }) =>
        user?.programAccess?.some(({ program }) => program === id),
      ),
    [programs, user?.programAccess],
  );
};

// CREDIT: https://usehooks.com/useMemoCompare/
const useMemoCompare = (next, compareFn) => {
  const ref = useRef();
  const value = ref.current;

  const isEqual = _.isEqualWith(compareFn)(value)(next);

  useEffect(() => {
    if (!isEqual) {
      ref.current = next;
    }
  });

  return isEqual ? value : next;
};

const useSize = (ref) => {
  const [size, setSize] = useState();

  useLayoutEffect(() => {
    ref && setSize(ref.getBoundingClientRect());
  }, [ref]);

  useResizeObserver(ref, (entry) => setSize(entry.contentRect));
  return size;
};

const useSessionStore = (type) => {
  const stores = {
    datagrid: DataGridStore,
    workspace: WorkspaceLayoutStore,
  };

  const [stream, setStream] = useSessionStorage(type);

  useEffect(() => {
    const subscription = stores[type].subscribe({
      next: setStream,
    });

    return () => subscription.unsubscribe();
  }, [type]);

  return [stream, stores[type]];
};

const useStore = (type) => {
  const stores = {
    item: ItemStore,
    passage: PassageStore,
    search: SearchStore,
  };

  const [stream, setStream] = useState();

  useEffect(() => {
    const subscription = stores[type.toLowerCase()].subscribe({
      next: setStream,
    });
    return () => subscription.unsubscribe();
  }, [type]);

  return [stream, stores[type]];
};

const useTabs = (initial) => {
  const [tab, setTab] = useState(initial);

  const changeTab = (_e, t) => {
    setTab(t);
  };

  return [tab, changeTab, setTab];
};

export {
  useDraggableInPortal,
  useFindPreviousValue,
  useGetMetadataConfigurationOptions,
  useGetProgramsByUser,
  useMemoCompare,
  useSessionStore,
  useSize,
  useStore,
  useTabs,
};
